/**
 * Demo personas (19 Oct): "Demo as…" per browser TAB, on test systems only.
 *
 * Rusty's 22 Oct demo walks one access case through the process across tabs,
 * each playing a different role. "View as" is a cookie, so one per browser;
 * a persona is a signed token the tab keeps in sessionStorage and sends on
 * every API call as {@link DEMO_PERSONA_HEADER}. The server honours it only
 * off production, only for an admin on VIEW_AS_EMAILS, only with that
 * admin's session; a "[UAT TEST]" persona may act, anyone else is read-only.
 *
 * Plain functions with no imports, so the node tests can run them directly
 * (`tests/demo-persona.test.mjs`).
 */

/** The header the API reads the tab's persona from. */
export const DEMO_PERSONA_HEADER = "X-HelpDesk-Demo-Persona";

/** The error code the API answers with when the tab's persona no longer holds. */
export const DEMO_PERSONA_INVALID_CODE = "DEMO_PERSONA_INVALID";

/** The roles of the access process a persona plays. */
export const DEMO_ROLES = [
  "hr",
  "global_it",
  "backoffice",
  "approver",
  "manager",
  "other",
] as const;
export type DemoRole = (typeof DEMO_ROLES)[number];

/** What the tab remembers: the token to send, and who it is for the banner. */
export interface DemoPersona {
  token: string;
  role: DemoRole;
  name: string;
  email: string;
  /** A "[UAT TEST]" identity: may act. Anyone else is read-only. */
  can_act: boolean;
  /** ISO timestamp: the server stops honouring it then (or with the session). */
  expires_at: string;
}

function isRole(value: unknown): value is DemoRole {
  return typeof value === "string" && (DEMO_ROLES as readonly string[]).includes(value);
}

/** The stored persona, or null when absent, malformed or run out. */
export function parseDemoPersona(
  raw: string | null | undefined,
  now: number = Date.now(),
): DemoPersona | null {
  if (!raw) {
    return null;
  }
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const p = data as Record<string, unknown>;
  if (
    typeof p.token !== "string" ||
    p.token === "" ||
    !isRole(p.role) ||
    typeof p.name !== "string" ||
    typeof p.email !== "string" ||
    typeof p.expires_at !== "string"
  ) {
    return null;
  }
  const ends = Date.parse(p.expires_at);
  if (Number.isNaN(ends) || ends <= now) {
    return null;
  }
  return {
    token: p.token,
    role: p.role,
    name: p.name,
    email: p.email,
    can_act: p.can_act === true,
    expires_at: p.expires_at,
  };
}

/** The headers a request carries for the tab's persona: none without one. */
export function demoPersonaHeaders(persona: DemoPersona | null): Record<string, string> {
  return persona === null ? {} : { [DEMO_PERSONA_HEADER]: persona.token };
}

/** Whether an API error body says the tab's persona has ended. */
export function isDemoPersonaInvalid(body: unknown): boolean {
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { code?: unknown }).code === DEMO_PERSONA_INVALID_CODE
  );
}

/**
 * The banner's colour per role, so the audience sees who's who at a glance.
 * Design-system categorical colours (all dark enough for white text).
 */
export const DEMO_ROLE_COLOUR: Record<DemoRole, string> = {
  hr: "bg-categorical-4",
  global_it: "bg-categorical-1",
  backoffice: "bg-categorical-2",
  approver: "bg-categorical-3",
  manager: "bg-categorical-5",
  other: "bg-bg-inverse",
};

/** Whether to offer "Demo as…": the API says this admin may, here (never in production). */
export function mayOfferDemoAs(me: { can_demo_as?: boolean | null } | null | undefined): boolean {
  return me?.can_demo_as === true;
}
